/**
 * @file Turnover — a unit moving from one tenancy to the next (PLAN.md §10
 * T2.10): the per-unit timeline `lease end → make-ready → next lease start`.
 *
 * ## What lives here, and what does not
 *
 * Four things, and they are one decision seen from four angles: *which tenancy
 * governs a unit today, when does it end, what is signed next, and where is the
 * gap?*
 *
 *   1. **The calendar arithmetic** (`daysUntilDate`), which is a difference of
 *      LOCAL dates in the PROPERTY's zone. `properties.timezone` already decides
 *      which day rent falls on (§6.4, T1.4); the same rule decides "days until
 *      this lease ends", and the same helper reads it (`localDateInZone`, never
 *      `toISOString()`).
 *   2. **The gap between two terms** (`turnoverGap`) — vacant days when the next
 *      tenancy starts after the last one ended, back-to-back when it starts the
 *      next day, and an OVERLAP when two agreements cover the same days. The plan
 *      calls overlapping leases first-class, so an overlap is computed and
 *      reported rather than smoothed away.
 *   3. **The position of one unit** (`unitTurnoverPosition`), a pure decision over
 *      lease facts. The successor question is NOT re-derived here: the position
 *      calls `leaseStatusAtEndOfTerm` (`./leases.js`, §6.2's successor guard), so
 *      the "does this unit's future exist yet" rule has exactly one home.
 *   4. **The DTOs** the API returns and the SPA renders.
 *
 * ## What this module deliberately cannot tell you
 *
 * No days on market, no average turnaround, no cost of turnover, no projected
 * rent, no "% occupied" trend. Every one of those needs history or policy that
 * does not exist in this database — a unit's vacancies are not recorded as events,
 * only as the space between two leases — and a rendered `0 days` or `$0.00` over
 * an empty set reads as a fact about the world rather than about the build. What
 * IS derivable from rows is stated in `TURNOVER_BASIS_REASON` and travels with the
 * payload so no screen has to remember it.
 *
 * ## Read-only
 *
 * This module contains no schema, no route and no write: every fact it needs
 * already exists (`units`, `leases`, `maintenance_requests`,
 * `properties.timezone`). There is deliberately no "make-ready task" entity — an
 * open maintenance request on a unit IS the make-ready work (see
 * `OPEN_MAINTENANCE_STATUSES`).
 *
 * The kernel discipline applies as everywhere in `packages/shared`: no drizzle, no
 * app code. Money is INTEGER CENTS; this module reads `rentAmountCents` and never
 * converts it.
 */
import { leaseStatusAtEndOfTerm, type LeaseMode, type LeaseStatus } from "./leases.js";
import { inclusiveDays, localDateInZone } from "./rent-charges.js";
import type { MaintenanceStatus } from "./maintenance.js";
import type { UnitAmenity } from "./properties.js";

/**
 * The §6.2 states in which a tenancy is IN FORCE.
 *
 * Taken from the state machine rather than from the dates, because the machine is
 * the authority on what a state means: `month_to_month` is a live tenancy whose
 * fixed term has run out (which is why its recorded end date is in the past and the
 * unit is still occupied), and `terminating` is a tenancy on its way out but still
 * in force. `draft` has not begun; `ended`, `deposit_reconciliation` and `closed`
 * are over.
 *
 * This list is what "the unit has NO ACTIVE LEASE AT ALL" is computed from.
 */
export const LIVE_LEASE_STATUSES = [
  "active",
  "month_to_month",
  "renewing",
  "terminating",
] as const;

/** Every §6.2 state, the ones above included, so a new state must be placed. */
export type LiveLeaseStatus = (typeof LIVE_LEASE_STATUSES)[number];

/** Is a lease in this state in force today? The one question the list answers. */
export function isLiveLeaseStatus(status: LeaseStatus): status is LiveLeaseStatus {
  return (LIVE_LEASE_STATUSES as readonly LeaseStatus[]).includes(status);
}

/**
 * The §6.3 states in which a repair is still OPEN — the definition of make-ready
 * work on this page.
 *
 * `completed` is done and `cancelled` was called off, so neither is work in front
 * of a landlord. `reopened` IS open: the repair did not hold, which is a task
 * somebody still has to do.
 */
export const OPEN_MAINTENANCE_STATUSES = [
  "submitted",
  "acknowledged",
  "in_progress",
  "reopened",
] as const;

export type OpenMaintenanceStatus = (typeof OPEN_MAINTENANCE_STATUSES)[number];

/** Is this repair still work in front of somebody? */
export function isOpenMaintenanceStatus(
  status: MaintenanceStatus,
): status is OpenMaintenanceStatus {
  return (OPEN_MAINTENANCE_STATUSES as readonly MaintenanceStatus[]).includes(status);
}

/**
 * What is happening to a unit, as ONE label — in the order the timeline reads.
 *
 * The order is the narrative PLAN.md §10 T2.10 draws (`lease end → make-ready →
 * next lease start`), and it is deliberately not the order of the enum's
 * declaration below. A unit is exactly one of these five at a time; the first three
 * are the units IN TRANSITION, and the last two are why a unit is not on the
 * timeline at all.
 */
export const TURNOVER_UNIT_KINDS = [
  "vacant",
  "ending",
  "successor_signed",
  "occupied",
  "no_lease",
] as const;

export type TurnoverUnitKind = (typeof TURNOVER_UNIT_KINDS)[number];

/**
 * The three kinds that ARE a transition: what ended, what is ending, and what has
 * a next tenancy signed. Exported so the payload's count and the screen's grouping
 * cannot disagree about which rows the page is about.
 */
export const IN_TRANSITION_UNIT_KINDS = [
  "vacant",
  "ending",
  "successor_signed",
] as const satisfies readonly TurnoverUnitKind[];

/** Human labels for the five kinds — the section headings of the timeline. */
export const TURNOVER_UNIT_KIND_LABELS: Readonly<Record<TurnoverUnitKind, string>> = {
  vacant: "Nothing in force",
  ending: "Tenancy ending",
  successor_signed: "Next tenancy signed",
  occupied: "Tenancy running",
  no_lease: "No lease recorded",
};

/**
 * What this page does not show, in one sentence, carried in the payload.
 *
 * The rule the whole product holds to: a figure the platform cannot compute is not
 * rendered as a zero. A turnover dashboard is the surface where that temptation is
 * strongest (every comparable product shows "days on market" and "average
 * turnaround"), so the refusal is stated in the API's own words and rendered beside
 * the timeline.
 */
export const TURNOVER_BASIS_REASON =
  "a read of the tenancies and repairs recorded in this portfolio: which lease is " +
  "in force on each unit, when it ends, what is signed next, and which repairs are " +
  "still open. It is not days on market, not an average turnaround, not a cost of " +
  "turnover and not projected rent — the platform records none of those, and a " +
  "rendered zero over an empty set would read as a fact about the world.";

// ---------------------------------------------------------------------------
// Calendar arithmetic — the property's calendar, never the server's
// ---------------------------------------------------------------------------

/**
 * **Days from the local calendar date at `instant` to `date`, in `timeZone`.**
 *
 * Positive when `date` is in the future, ZERO on the day itself, negative once it
 * has passed. This is the function T1.4's rule turns on, applied to a lease end
 * date: "days until the end" is a difference of LOCAL calendar days in the
 * building's zone, not of server instants. `new Date().toISOString().slice(0,10)`
 * answers in UTC, which is a different day from the property's for part of every
 * day — so the local date comes from `localDateInZone` (`./rent-charges.js`, the
 * one reader of `Intl` with an explicit zone) and never from an ISO string's prefix.
 *
 * `inclusiveDays(a, b)` counts BOTH end days, so the same day is 1 and the
 * difference is one less.
 */
export function daysUntilDate(instant: Date, date: string, timeZone: string): number {
  return inclusiveDays(localDateInZone(instant, timeZone), date) - 1;
}

// ---------------------------------------------------------------------------
// The gap between two terms
// ---------------------------------------------------------------------------

/** Which shape the space between two terms has. */
export const TURNOVER_GAP_KINDS = ["back_to_back", "gap", "overlap"] as const;

export type TurnoverGapKind = (typeof TURNOVER_GAP_KINDS)[number];

export interface TurnoverGap {
  readonly kind: TurnoverGapKind;
  /** Whole days NO lease covers. 0 for back-to-back and for an overlap. */
  readonly vacantDays: number;
  /** Whole days BOTH agreements cover. 0 unless two terms collide. */
  readonly overlapDays: number;
  /** One sentence saying what the arithmetic found. */
  readonly reason: string;
}

/**
 * **The space between one tenancy's last day and the next one's first day.**
 *
 * Both `leases.start_date` and `leases.end_date` are inclusive calendar days (§6.4
 * bills a period that ends on its own last day), so the ordinary succession — one
 * lease ending Aug 31 and the next starting Sep 1 — is a span of TWO days inclusive
 * and zero vacant days. Anything after that is a day nobody is paying for, and
 * anything before it is a day two agreements both cover: an OVERLAP.
 *
 * The overlap is reported rather than hidden because PLAN.md §10 T2.10 calls
 * overlapping leases first-class, and because two terms on one unit is exactly the
 * state §6.2's successor guard exists to keep out of the status column — when the
 * data says it happened anyway, the page a landlord reads should say so too.
 */
export function turnoverGap(currentEndDate: string, successorStartDate: string): TurnoverGap {
  const span = inclusiveDays(currentEndDate, successorStartDate);

  if (span >= 2) {
    const vacantDays = span - 2;
    return vacantDays === 0
      ? {
          kind: "back_to_back",
          vacantDays: 0,
          overlapDays: 0,
          reason: `the next tenancy starts the day after this one ends (${currentEndDate} → ${successorStartDate}), so no day is vacant`,
        }
      : {
          kind: "gap",
          vacantDays,
          overlapDays: 0,
          reason: `${String(vacantDays)} day${vacantDays === 1 ? "" : "s"} between the end of this term (${currentEndDate}) and the start of the next (${successorStartDate})`,
        };
  }

  // span <= 1: the successor starts on the current term's last day or before it,
  // so the two agreements cover the same days.
  const overlapDays = 2 - span;
  return {
    kind: "overlap",
    vacantDays: 0,
    overlapDays,
    reason: `this term and the next OVERLAP on ${String(overlapDays)} day${overlapDays === 1 ? "" : "s"} (${currentEndDate} / ${successorStartDate}) — two agreements cover the same days`,
  };
}

// ---------------------------------------------------------------------------
// One unit's position
// ---------------------------------------------------------------------------

/**
 * The lease facts the decision reads. Facts, not rows — so the whole decision is
 * testable with no database, which is what makes the trickiest cases (a tenancy
 * that ended, a unit that never had one) assertable.
 */
export interface TurnoverLeaseFact {
  readonly id: string;
  readonly status: LeaseStatus;
  readonly startDate: string;
  readonly endDate: string;
}

/** The facts one unit's position is decided from. */
export interface UnitTurnoverFacts {
  /** Every lease on THIS unit, in any state. */
  readonly leases: readonly TurnoverLeaseFact[];
  /** The instant "today" is read from. */
  readonly now: Date;
  /** The PROPERTY's IANA zone — the calendar the countdown is measured on. */
  readonly timeZone: string;
  /** How many OPEN maintenance requests are logged against this unit. */
  readonly openRequests: number;
}

/**
 * What §6.2's successor guard decides for a lease at its end date.
 *
 * Only computed for a lease in `active`, which is the ONE state §6.2 writes the
 * guard for (`active → month_to_month` OR `active → ended`). A lease that has
 * already ended has no decision left to make, and reporting a verdict for it would
 * be reporting a move that is not available.
 */
export interface TurnoverEndOfTerm {
  readonly status: "month_to_month" | "ended";
  readonly successorLeaseExists: boolean;
  readonly terminationScheduled: boolean;
}

export interface UnitTurnoverPosition {
  /** Today's date in the property's zone, `YYYY-MM-DD`. */
  readonly asOfDate: string;
  /** The tenancy that governs this unit today — see `unitTurnoverPosition`. */
  readonly currentLeaseId: string | null;
  /** The NEXT agreement recorded on this unit, if any. */
  readonly successorLeaseId: string | null;
  readonly hasActiveLease: boolean;
  /** Signed whole days until the current term ends; negative once it has. */
  readonly daysUntilCurrentLeaseEnd: number | null;
  /** The space between the two terms — only when both exist. */
  readonly gap: TurnoverGap | null;
  /** The §6.2 guard's verdict, or `null` when no move is available. */
  readonly endOfTerm: TurnoverEndOfTerm | null;
  readonly kind: TurnoverUnitKind;
  readonly inTransition: boolean;
  /** One sentence saying why this unit is where it is. */
  readonly reason: string;
}

/**
 * Order two leases the way the timeline reads them: the later start first, then the
 * later end, then the id. The id tiebreak is what makes the choice TOTAL — two
 * leases on the same dates cannot flap between two renders.
 */
function isLater(a: TurnoverLeaseFact, b: TurnoverLeaseFact): boolean {
  if (a.startDate !== b.startDate) return a.startDate > b.startDate;
  if (a.endDate !== b.endDate) return a.endDate > b.endDate;
  return a.id > b.id;
}

/** The latest-starting lease of a set, or `null` when the set is empty. */
function latestStarting(
  leases: readonly TurnoverLeaseFact[],
): TurnoverLeaseFact | null {
  let best: TurnoverLeaseFact | null = null;
  for (const lease of leases) {
    if (best === null || isLater(lease, best)) best = lease;
  }
  return best;
}

/**
 * **Which lease on a unit is the CURRENT one** — the question a unit's own page
 * asks, and the answer is the §6.2 state machine's, not the calendar's.
 *
 * A unit can legitimately carry more than one lease (a successor signed during
 * turnover), so "the current lease" cannot be "the lease". The rule is:
 *
 *   1. keep the leases whose status is IN FORCE — `LIVE_LEASE_STATUSES`, the same
 *      list the Turnover page computes "this unit has no active lease at all"
 *      from, so the two pages cannot disagree about what a live tenancy is;
 *   2. of those, take the LATEST STARTING one.
 *
 * `draft` is excluded because a signed-but-unstarted agreement earns nothing yet,
 * and `ended` / `deposit_reconciliation` / `closed` are history. `month_to_month`
 * and `terminating` are IN, because both are tenancies still in force — a
 * month-to-month term has a recorded end date in the past and is still occupied,
 * and a terminating tenancy has given notice but not left.
 *
 * This deliberately is NOT `unitTurnoverPosition().currentLeaseId`. That one adds a
 * `start_date <= today` gate because a turnover countdown needs a term that has
 * BEGUN; a unit page must not say "nothing is in force here" merely because a
 * tenancy's first day is tomorrow. The two answer different questions and are kept
 * apart on purpose — the shared half, which is the part that could drift, is the
 * status list, and both read it from here.
 *
 * The same total tiebreak `unitTurnoverPosition` uses applies: later start, then
 * later end, then the id, so two leases on identical dates cannot flap between two
 * renders.
 */
export function currentLiveLeaseId(
  leases: readonly TurnoverLeaseFact[],
): string | null {
  const inForce = leases.filter((lease) => isLiveLeaseStatus(lease.status));
  return latestStarting(inForce)?.id ?? null;
}

/** The earliest-starting lease of a set, or `null` when the set is empty. */
function earliestStarting(
  leases: readonly TurnoverLeaseFact[],
): TurnoverLeaseFact | null {
  let best: TurnoverLeaseFact | null = null;
  for (const lease of leases) {
    if (best === null || isLater(best, lease)) best = lease;
  }
  return best;
}

/**
 * **Where one unit stands between tenancies**, decided from lease facts alone.
 *
 * Three answers are load-bearing:
 *
 *   * **the current lease** is the latest-starting lease that has BEGUN
 *     (`start_date <= today`) and is not a `draft` or `closed` one. A draft has not
 *     begun, so a signed successor is never mistaken for the current tenancy; a
 *     closed lease is history. `ended` and `deposit_reconciliation` ARE candidates,
 *     because a tenancy that has just finished is exactly what a turnover page is
 *     about — dropping it would hide the make-ready gap the page exists to show.
 *   * **the successor** is the earliest-starting NON-CLOSED lease that begins after
 *     the current one (`null` current → the earliest non-closed lease at all). That
 *     is the guard's own reading of "a successor lease exists on the unit": a
 *     renewal that is a draft, active, or anything else that is not closed.
 *   * **`hasActiveLease`** is computed from the STATE MACHINE, not from the dates
 *     (`LIVE_LEASE_STATUSES`). A `month_to_month` tenancy is in force with a
 *     recorded end date in the past — that is what rolling over means — so a
 *     date-based answer would call an occupied unit vacant.
 *
 * The kind then follows, in this order: nothing recorded at all (`no_lease`); the
 * next tenancy is signed (`successor_signed`); nothing is in force (`vacant`); the
 * tenancy in force is winding down (`ending`); otherwise a term is simply running
 * (`occupied`). The first three are `inTransition`; the last two are why a unit is
 * not on the timeline.
 */
export function unitTurnoverPosition(facts: UnitTurnoverFacts): UnitTurnoverPosition {
  const asOfDate = localDateInZone(facts.now, facts.timeZone);
  const hasActiveLease = facts.leases.some((lease) => isLiveLeaseStatus(lease.status));

  if (facts.leases.length === 0) {
    return {
      asOfDate,
      currentLeaseId: null,
      successorLeaseId: null,
      hasActiveLease,
      daysUntilCurrentLeaseEnd: null,
      gap: null,
      endOfTerm: null,
      kind: "no_lease",
      inTransition: false,
      reason:
        facts.openRequests > 0
          ? `no tenancy has ever been recorded on this unit, so there is no transition to show — ${String(facts.openRequests)} open maintenance request${facts.openRequests === 1 ? "" : "s"} ${facts.openRequests === 1 ? "is" : "are"} logged against it`
          : "no tenancy has ever been recorded on this unit, so there is no transition to show",
    };
  }

  const began = facts.leases.filter(
    (lease) => lease.status !== "draft" && lease.status !== "closed" && lease.startDate <= asOfDate,
  );
  const current = latestStarting(began);

  const others = facts.leases.filter(
    (lease) =>
      lease.status !== "closed" &&
      lease.id !== current?.id &&
      (current === null || lease.startDate > current.startDate),
  );
  const successor = earliestStarting(others);

  const hasSuccessor = successor !== null;
  const terminationScheduled = current?.status === "terminating";

  // §6.2's successor guard, REUSED rather than re-derived. It is evaluated only for
  // an `active` lease, which is the one state the plan writes the move for.
  //
  // `terminationScheduled` is false on every row the guard is evaluated for, and
  // that is a fact about the schema rather than a shortcut: `terminating` is the
  // ONLY representation of a scheduled termination, and an `active` lease is not in
  // it. It is passed as a named fact rather than hardcoded so that the input the
  // guard reads is visible here.
  const endOfTerm: TurnoverEndOfTerm | null =
    current !== null && current.status === "active"
      ? {
          status: leaseStatusAtEndOfTerm({
            successorLeaseExists: hasSuccessor,
            terminationScheduled,
          }),
          successorLeaseExists: hasSuccessor,
          terminationScheduled,
        }
      : null;

  const daysUntilCurrentLeaseEnd =
    current === null ? null : daysUntilDate(facts.now, current.endDate, facts.timeZone);
  const gap =
    current === null || successor === null
      ? null
      : turnoverGap(current.endDate, successor.startDate);

  const windingDown =
    current !== null &&
    (current.status === "month_to_month" || current.status === "terminating");

  let kind: TurnoverUnitKind;
  let reason: string;

  if (successor !== null) {
    kind = "successor_signed";
    // The gap is NOT folded into this sentence: it is its own fact with its own
    // explanation (`gap.reason`), and a caller rendering both would print the same
    // arithmetic twice.
    reason = `the next tenancy is already recorded on this unit, starting ${successor.startDate}${
      current === null
        ? " — no earlier tenancy has begun"
        : `; the current lease ends ${current.endDate}`
    }`;
  } else if (!hasActiveLease) {
    kind = "vacant";
    reason = `${
      current === null
        ? "no lease on this unit is in force — every one recorded is a draft or already closed, so nothing covers it today"
        : `no lease on this unit is in force (the most recent is ${current.status}), so nothing covers it today`
    }${
      facts.openRequests > 0
        ? ` and ${String(facts.openRequests)} open maintenance request${facts.openRequests === 1 ? "" : "s"} ${facts.openRequests === 1 ? "is" : "are"} logged against it`
        : ""
    }`;
  } else if (
    current !== null &&
    (windingDown || current.endDate < asOfDate)
  ) {
    // `windingDown` narrows nothing (it is a boolean local), so the null check is
    // written out: the branch is only reachable with a lease in hand.
    kind = "ending";
    reason =
      current.status === "month_to_month"
        ? `the fixed term ran out on ${current.endDate} and §6.2 rolled this tenancy to month-to-month, so nothing is signed for next year yet`
        : current.status === "terminating"
          ? `the tenancy is terminating, so its end date (${current.endDate}) is the day to plan around`
          : `the lease in force (${current.status}) passed its end date on ${current.endDate} and has not moved, so it is still what governs this unit`;
  } else if (current === null) {
    // Unreachable through the branches above with live leases present, and kept as
    // an explicit guard so no path can fall out of the classification.
    kind = "occupied";
    reason =
      "a lease is recorded on this unit but none has begun, so there is nothing in force yet";
  } else {
    kind = "occupied";
    reason = `a tenancy is in force and its end date (${current.endDate}) is still ahead; no successor has been recorded`;
  }

  return {
    asOfDate,
    currentLeaseId: current?.id ?? null,
    successorLeaseId: successor?.id ?? null,
    hasActiveLease,
    daysUntilCurrentLeaseEnd,
    gap,
    endOfTerm,
    kind,
    inTransition: (IN_TRANSITION_UNIT_KINDS as readonly TurnoverUnitKind[]).includes(kind),
    reason,
  };
}

// ---------------------------------------------------------------------------
// DTOs — what the API returns
// ---------------------------------------------------------------------------

/** A lease on the timeline: enough to place it, and no more. */
export interface TurnoverLeaseDto {
  readonly leaseId: string;
  readonly status: LeaseStatus;
  readonly startDate: string;
  readonly endDate: string;
}

/**
 * The tenancy that governs the unit today, with what it is owed.
 *
 * `rentAmountCents` is integer cents, straight from `leases.rent_amount_cents` —
 * the AGREEMENT's rent, not a projection and not what was collected.
 */
export interface TurnoverCurrentLeaseDto extends TurnoverLeaseDto {
  readonly rentAmountCents: number;
}

/**
 * The next tenancy recorded on the unit.
 *
 * Deliberately WITHOUT its end date or its rent: this is a draft or a signed future
 * term, and the question the page answers is "does the next lease exist, and when
 * does it start". Its terms are the Leases screen's business.
 */
export interface TurnoverSuccessorLeaseDto {
  readonly leaseId: string;
  readonly status: LeaseStatus;
  readonly startDate: string;
}

/**
 * One open repair on the unit — the make-ready half of the timeline.
 *
 * `leaseId` is `null` on a repair logged against the unit with NO tenancy, which is
 * what a make-ready request actually is: there is no make-ready entity in this
 * schema, and a repair on a vacant unit IS the make-ready work.
 */
export interface TurnoverMaintenanceItemDto {
  readonly requestId: string;
  readonly title: string;
  readonly status: MaintenanceStatus;
  readonly leaseId: string | null;
  readonly emergency: boolean;
}

export interface TurnoverOpenMaintenanceDto {
  readonly requests: number;
  /** How many the caller flagged as an emergency — the flag, counted. */
  readonly emergency: number;
  readonly items: readonly TurnoverMaintenanceItemDto[];
}

/** One unit, its property context, its timeline and its open work. */
export interface TurnoverUnitRowDto {
  readonly unitId: string;
  readonly label: string;
  readonly propertyId: string;
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
  /** The zone this row's day counts were measured in. */
  readonly timezone: string;
  /** Today in THAT zone, `YYYY-MM-DD` — the day the countdown is measured from. */
  readonly asOfDate: string;
  readonly currentLease: TurnoverCurrentLeaseDto | null;
  readonly successorLease: TurnoverSuccessorLeaseDto | null;
  readonly hasActiveLease: boolean;
  /** Signed whole days; `null` when there is no current lease at all. */
  readonly daysUntilCurrentLeaseEnd: number | null;
  readonly gap: TurnoverGap | null;
  readonly endOfTerm: TurnoverEndOfTerm | null;
  readonly kind: TurnoverUnitKind;
  readonly inTransition: boolean;
  readonly transitionReason: string;
  readonly openMaintenance: TurnoverOpenMaintenanceDto;
}

/**
 * One lease on a unit, as the unit's own page lists it.
 *
 * `TurnoverLeaseDto` plus the two facts a landlord decides on here: the rent (the
 * AGREEMENT's, in integer cents) and the mode. Both are already on the lease row, so
 * this is not a derived figure — it is the row, narrowed to what the page renders.
 */
export interface UnitLeaseSummaryDto extends TurnoverLeaseDto {
  readonly rentAmountCents: number;
  readonly mode: LeaseMode;
}

/**
 * One maintenance request on a unit — open or past.
 *
 * `leaseId` is `null` on a repair logged against the unit with NO tenancy, which is
 * what a make-ready request actually is: there is no make-ready entity in this
 * schema, and a repair on a vacant unit IS the make-ready work.
 */
export interface UnitMaintenanceSummaryDto {
  readonly id: string;
  readonly title: string;
  readonly status: MaintenanceStatus;
  readonly emergency: boolean;
  /** Open (still work in front of somebody) or past, per the §6.3 open set. */
  readonly open: boolean;
  /** The tenancy that reported it, or `null` for a repair on the unit itself. */
  readonly leaseId: string | null;
  /** The instant the request was logged. */
  readonly createdAt: string;
}

/**
 * GET /workspaces/:workspaceId/units/:unitId — one unit and the context a unit page
 * needs, in ONE round trip.
 *
 * **The breadcrumb chain travels in the payload** (`propertyLabel`, `addressLine1`,
 * `city`, `state`): a `Properties › 123 N Main St › Unit 2` line that needed a second
 * request for the building's name would flash an incomplete chain on every visit —
 * the same reasoning `GET /properties/:propertyId` returns its units inline.
 *
 * `leases` is a LIST and not a single "current lease", because a unit legitimately
 * carries a successor during turnover. `currentLeaseId` names the one in force (see
 * `currentLiveLeaseId`), and is `null` when nothing is — which is a real state a
 * landlord acts on ("this unit earns nothing"), not an absence of data.
 *
 * **No second timezone-relative figure is derived here.** The one date-derived fact a
 * unit page could carry — "is the current term past its end date" — belongs to the
 * Turnover page, which owns that arithmetic in the building's zone. Inventing a
 * second, differently-gated version of it is how the two surfaces would start
 * disagreeing, so this payload reports the rows and says which one is in force.
 */
export interface UnitDetailDto {
  readonly id: string;
  readonly label: string;
  readonly beds: number | null;
  readonly baths: number | null;
  readonly amenities: readonly UnitAmenity[];
  readonly propertyId: string;
  readonly propertyLabel: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
  readonly timezone: string;
  /** Every lease on this unit, newest term first. */
  readonly leases: readonly UnitLeaseSummaryDto[];
  /** The lease in force, or `null` when none is — see `currentLiveLeaseId`. */
  readonly currentLeaseId: string | null;
  readonly maintenance: readonly UnitMaintenanceSummaryDto[];
  readonly counts: {
    readonly leases: number;
    readonly openMaintenance: number;
    readonly pastMaintenance: number;
  };
}

/**
 * GET /workspaces/:workspaceId/turnover — the whole portfolio, classified.
 *
 * `units` carries EVERY unit in the portfolio, each with its derived position, and
 * `counts` describes those rows: a unit that is not in transition is still returned,
 * with the reason it is not, because "no active lease at all" is a fact a landlord
 * needs and a filtered list cannot state. The SCREEN shows the units in transition
 * and says how many others there are.
 */
export interface TurnoverListDto {
  /** True when the list was cut at the server cap: these are the first rows, not all of them. */
  readonly truncated?: true;
  readonly counts: {
    /** Every unit considered — the denominator of `inTransition`. */
    readonly units: number;
    /** The figure the page leads with. */
    readonly inTransition: number;
    /** Units with a next tenancy recorded. */
    readonly withSuccessor: number;
    /** Units with no lease in force, whether or not one ever existed. */
    readonly withoutActiveLease: number;
    /** Open repairs across the returned units — the make-ready work in view. */
    readonly openRequests: number;
  };
  /** Only the kinds that actually occur, in timeline order. */
  readonly byKind: readonly {
    readonly kind: TurnoverUnitKind;
    readonly units: number;
  }[];
  /** The instant this payload was computed at. */
  readonly asOf: string;
  /** What the payload is not — see `TURNOVER_BASIS_REASON`. */
  readonly basis: string;
  readonly units: readonly TurnoverUnitRowDto[];
}
